import { Link } from 'react-router-dom';
import type { LinhaDoTempoDTO } from '@adega/shared';
import { usePreferencias, useRetrato } from '../api/queries.js';
import { Barras, Secao } from './Grafico.js';

/**
 * Quando a adega vence.
 *
 * Saiu de "Sua adega em números" quando conselho e contagem viraram telas
 * separadas: contar o que existe é uma pergunta, saber o que fazer com isso é
 * outra. Continua sendo conta pura — quem opina é a análise, logo abaixo dela.
 */
export function LinhaDoTempo() {
  const { data: retrato } = useRetrato();
  // O ritmo mora nas preferências; a linha já vem calculada com ele, mas a
  // frase repete o número para a conta ficar auditável.
  const { data: preferencias } = usePreferencias();

  if (!retrato || retrato.garrafas === 0) return null;
  const linha = retrato.linhaDoTempo;

  return (
    <div className="card">
      <Secao icone="⏳" titulo="Quando vence">
        <div className="space-y-1">
          {frasesDaLinha(linha, preferencias?.garrafasPorMes ?? null).map(
            (frase) => (
              <p key={frase} className="text-sm leading-relaxed">
                {frase}
              </p>
            ),
          )}
        </div>

        {anosComGarrafa(linha).length > 0 ? (
          <div className="mt-3">
            <Barras
              itens={anosComGarrafa(linha).map((ano) => ({
                rotulo: String(ano.ano),
                valor: ano.fecham,
              }))}
              cor="bg-adega-gold/70"
            />
            <p className="mt-1 text-xs text-adega-muted">Garrafas que fecham a janela em cada ano.</p>
          </div>
        ) : null}

        <div className="mt-3 space-y-1 text-xs text-adega-muted">
          {linha.mesesDeEstoque !== null ? (
            <p>
              {/* A condição não é rodapé jurídico: sem ela o número vira
                  "a adega acaba em N meses", e ele compra enquanto bebe. */}
              Se você parasse de comprar hoje, o que está guardado duraria cerca de{' '}
              {linha.mesesDeEstoque}{' '}
              {linha.mesesDeEstoque === 1 ? 'mês' : 'meses'}. As contas acima
              olham só o que já está na adega — garrafa nova disputa a mesma capacidade de
              beber.
            </p>
          ) : (
            // Sem ritmo a tela ainda mostra os anos, mas não afirma perda:
            // seria inventar um número que ninguém informou.
            <p>
              <Link to="/perfil" className="underline">
                Diga quantas garrafas você bebe por mês
              </Link>{' '}
              e eu digo quantas não vão ser alcançadas a tempo.
            </p>
          )}
          {linha.semPico > 0 ? (
            <p>
              {linha.semPico}{' '}
              {linha.semPico === 1
                ? 'garrafa está sem momento estimado'
                : 'garrafas estão sem momento estimado'}{' '}
              e ficaram de fora da conta de auge.
            </p>
          ) : null}
          {linha.semJanela > 0 ? (
            <p>
              {linha.semJanela}{' '}
              {linha.semJanela === 1 ? 'garrafa está' : 'garrafas estão'} sem
              janela estimada e ficaram de fora dos anos acima.
            </p>
          ) : null}
        </div>
      </Secao>
    </div>
  );
}

/**
 * Até dez anos de horizonte: com uma garrafa em 2041, o gráfico viraria uma
 * régua de quinze colunas vazias e o aperto de 2030 sumiria no meio.
 */
function anosComGarrafa(linha: LinhaDoTempoDTO): LinhaDoTempoDTO['anos'] {
  return linha.anos.slice(0, 10).filter((ano) => ano.fecham > 0);
}

/**
 * O diagnóstico em texto.
 *
 * A manchete é o PICO, e não o fim da janela. "66 prontas para abrir hoje" era
 * verdade e não servia para nada: o que se sente na taça é o momento do vinho,
 * e garrafa com janela até 2033 pode estar em queda desde 2024.
 */
function frasesDaLinha(linha: LinhaDoTempoDTO, garrafasPorMes: number | null): string[] {
  const frases: string[] = [];

  if (linha.passaramDoPico > 0) {
    frases.push(
      `${linha.passaramDoPico} ${
        linha.passaramDoPico === 1 ? 'garrafa já passou' : 'garrafas já passaram'
      } do melhor momento: a janela continua aberta, mas daqui em diante só perdem.`,
    );
  }
  if (linha.noPicoAgora > 0) {
    frases.push(
      `${linha.noPicoAgora} ${
        linha.noPicoAgora === 1 ? 'está no auge' : 'estão no auge'
      } agora — é a hora delas.`,
    );
  }
  if (linha.aindaEvoluem > 0) {
    frases.push(
      `${linha.aindaEvoluem} ainda ${
        linha.aindaEvoluem === 1 ? 'ganha' : 'ganham'
      } com o tempo.`,
    );
  }
  if (frases.length === 0) {
    frases.push('Nenhuma garrafa tem o momento estimado ainda.');
  }

  if (linha.maiorAperto !== null && linha.maiorAperto.garrafas > 1) {
    frases.push(
      `O aperto é em ${linha.maiorAperto.ano}: ${linha.maiorAperto.garrafas} garrafas fecham a janela no mesmo ano.`,
    );
  }

  if (linha.passamDoPonto !== null && garrafasPorMes !== null && linha.passamDoPonto > 0) {
    // Só aparece quando há perda: "dá para beber todas" era uma tranquilidade
    // falsa — a conta não sabe o que ele vai comprar amanhã.
    frases.push(
      `No seu ritmo de ${garrafasPorMes} por mês, ao menos ${linha.passamDoPonto} ${
        linha.passamDoPonto === 1 ? 'fecha a janela' : 'fecham a janela'
      } antes de chegar a vez ${linha.passamDoPonto === 1 ? 'dela' : 'delas'}.`,
    );
  }

  return frases;
}
